/**
 * IOSFilePreviewItem 组件
 * 显示单个文件的预览，提供文件操作（如删除），显示上传进度和状态
 * 采用iOS风格设计
 */
import React, { useEffect, useState, memo, useMemo } from 'react';
import { createFileSelector, abortSingleFileAtom } from '../../../../atoms/v3/uppyAtoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { getFileIconByType, getColorForExtension, getFileExtension } from '../../../../constants/fileTypeIcons';
import { useThemeMode } from '../../../../hooks/v0/useThemeMode';
import { motion } from 'framer-motion';

interface IOSFilePreviewItemProps {
  fileId?: string;
  // 消息模式：直接提供文件信息，无交互
  messageMode?: {
    url?: string;
    originalUrl?: string; // 原图URL，用于点击打开
    fileName: string;
    fileType: string;
    isImage: boolean;
  };
}

const IOSFilePreviewItemComponent: React.FC<IOSFilePreviewItemProps> = ({ fileId, messageMode }) => {
  const fileSelector = useMemo(() => fileId ? createFileSelector(fileId) : null, [fileId]);
  const file = fileSelector ? useAtomValue(fileSelector) : null;
  const abortSingleFile = useSetAtom(abortSingleFileAtom);
  const { isDarkMode } = useThemeMode();

  // 消息模式或上传模式
  const isMessageMode = !!messageMode;
  const isUploadMode = !!fileId && !!file;

  if (!isMessageMode && !isUploadMode) return null;

  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);

  // 处理文件预览URL
  useEffect(() => {
    if (isMessageMode && messageMode) {
      // 消息模式：图片使用缩略图，非图片使用文件类型图标
      if (messageMode.isImage) {
        setImageUrl(messageMode.url);
      } else {
        // 从文件名获取扩展名，使用文件类型图标
        const extension = messageMode.fileName.split('.').pop() || '';
        const iconUrl = getFileIconByType(extension);
        setImageUrl(iconUrl);
      }
    } else if (isUploadMode && file) {
      // 上传模式：从文件数据生成 URL
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file.data);
        setImageUrl(url);
        return () => {
          URL.revokeObjectURL(url);
          setImageUrl(undefined);
        };
      } else {
        // 从文件名获取扩展名
        const extension = file.name.split('.').pop() || '';
        const iconUrl = getFileIconByType(extension);
        setImageUrl(iconUrl);
      }
    }
  }, [isMessageMode, messageMode, isUploadMode, file]);

  // 处理文件删除/中止
  const handleAbort = () => {
    if (file) {
      abortSingleFile(file.id);
    }
  };

  // 获取文件状态
  const getFileStatus = () => {
    if (isMessageMode) {
      return 'completed'; // 消息模式下文件已完成
    }
    if (!file) return 'preparing';

    switch (file.uploadStatus) {
      case 'queued':
        return 'queued';
      case 'preparing':
      case 'uploading':
      case 'aborted':
      case 'success':
      case 'error':
        return file.uploadStatus;
      default:
        return 'preparing';
    }
  };

  const fileStatus = getFileStatus();
  const isImage = isMessageMode ? messageMode!.isImage : (file?.type.startsWith('image/') || false);
  const fileName = isMessageMode ? messageMode!.fileName : (file?.name || '');

  // 获取文件名的多行显示版本
  const getMultilineFileName = (name: string) => {
    const extension = name.split('.').pop() || '';
    const baseName = name.substring(0, name.length - (extension ? extension.length + 1 : 0));

    // 如果文件名很短，直接返回
    if (baseName.length <= 15) {
      return {
        displayName: name,
        lines: 1
      };
    }

    // 将文件名分成多行，最多3行
    const maxLineLength = 10; // 每行最多字符数
    const maxLines = 3; // 最多显示行数

    let lines: string[] = [];
    let currentLine = '';
    let remainingName = baseName;

    // 分割文件名到多行
    while (remainingName.length > 0 && lines.length < maxLines - 1) {
      if (remainingName.length <= maxLineLength) {
        // 如果剩余部分可以放入一行，直接添加
        lines.push(remainingName);
        remainingName = '';
      } else {
        // 否则截取一行长度
        currentLine = remainingName.substring(0, maxLineLength);
        lines.push(currentLine);
        remainingName = remainingName.substring(maxLineLength);
      }
    }

    // 处理最后一行（可能需要添加省略号）
    if (remainingName.length > 0) {
      if (remainingName.length <= maxLineLength) {
        lines.push(remainingName);
      } else {
        lines.push(remainingName.substring(0, maxLineLength - 3) + '...');
      }
    }

    // 如果有扩展名，添加到最后一行
    if (extension) {
      const lastLineIndex = lines.length - 1;
      // 检查最后一行加上扩展名是否会太长
      if (lines[lastLineIndex].length + extension.length + 1 <= maxLineLength) {
        lines[lastLineIndex] = lines[lastLineIndex] + '.' + extension;
      } else {
        // 如果太长，扩展名单独一行
        if (lines.length < maxLines) {
          lines.push('.' + extension);
        } else {
          // 如果已经达到最大行数，替换最后一行
          lines[lastLineIndex] = lines[lastLineIndex].substring(0, maxLineLength - extension.length - 4) + '...' + '.' + extension;
        }
      }
    }

    return {
      displayName: lines.join('\n'),
      lines: lines.length
    };
  };

  // 获取文件扩展名标签
  const getExtensionLabel = (fileName: string) => {
    const ext = getFileExtension(fileName);
    if (!ext) return null;

    // 获取扩展名的前2-4个字符（根据长度）
    let displayExt = ext;
    if (ext.length > 4) {
      displayExt = ext.substring(0, 4);
    } else if (ext.length < 2) {
      displayExt = ext.padEnd(2, ' ');
    }

    // 获取扩展名的颜色
    const color = getColorForExtension(ext);

    return {
      text: displayExt.toUpperCase(),
      color: color
    };
  };

  return (
    <motion.div
      className={`relative rounded-ios-lg overflow-hidden w-20 h-20 m-1 shadow-ios-sm ${
        isDarkMode ? 'bg-ios-dark-gray5' : 'bg-white'
      }`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={isMessageMode ? () => window.open(messageMode!.originalUrl || messageMode!.url, '_blank') : undefined}
      style={{ cursor: isMessageMode ? 'pointer' : 'default' }}
    >
      {/* 文件类型标签 */}
      {!isImage && (() => {
        const extLabel = getExtensionLabel(fileName);
        if (!extLabel) return null;

        return (
          <div
            className="absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded text-[8px] font-medium shadow-ios-sm text-white"
            style={{
              backgroundColor: extLabel.color, // 100% 不透明度
            }}
          >
            {extLabel.text}
          </div>
        );
      })()}

      {/* 文件预览/图标 */}
      <div className="w-full h-full flex items-center justify-center">
        {isImage && imageUrl ? (
          <img
            src={imageUrl}
            alt={fileName}
            className="w-full h-full object-cover"

          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-2">
            {imageUrl && (
              <img
                src={imageUrl}
                alt={fileName}
                className="w-10 h-10 object-contain"
              />
            )}
            {(() => {
              const { displayName, lines } = getMultilineFileName(fileName);
              return (
                <div
                  className={`text-[9px] mt-1 text-center w-full ${
                    lines > 1 ? 'leading-tight' : 'truncate'
                  } text-ios-gray dark:text-ios-dark-gray`}
                  style={{
                    maxHeight: '3em',
                    whiteSpace: lines > 1 ? 'pre-line' : 'nowrap'
                  }}
                >
                  {displayName}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* 上传状态指示器 - 仅上传模式显示 */}
      {!isMessageMode && fileStatus === 'uploading' && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-ios flex items-center justify-center">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <circle
                className="opacity-75"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray="60"
                strokeDashoffset={60 - (file?.progress || 0) * 0.6}
                fill="none"
                transform="rotate(-90 12 12)"
              />
              <text
                x="12"
                y="13"
                textAnchor="middle"
                fontSize="6"
                fill="currentColor"
                className="font-medium"
              >
                {Math.round(file?.progress || 0)}%
              </text>
            </svg>
          </div>
        </div>
      )}

      {/* 准备中状态 - 仅上传模式显示 */}
      {!isMessageMode && fileStatus === 'preparing' && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-ios flex items-center justify-center">
            <svg className="w-6 h-6 text-white animate-ios-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="none"
                strokeWidth="4"
                stroke="currentColor"
                d="M4 12a8 8 0 018-8"
              />
            </svg>
          </div>
        </div>
      )}

      {/* 队列中状态 - 仅上传模式显示 */}
      {!isMessageMode && fileStatus === 'queued' && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-ios flex items-center justify-center">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
        </div>
      )}

      {/* 成功状态 - 仅上传模式显示 */}
      {!isMessageMode && fileStatus === 'success' && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ios-green dark:bg-ios-dark-green flex items-center justify-center shadow-ios-sm">
          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      {/* 错误状态 - 仅上传模式显示 */}
      {!isMessageMode && fileStatus === 'error' && (
        <div className="absolute inset-0 bg-ios-red/30 dark:bg-ios-dark-red/30 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-ios-red/80 dark:bg-ios-dark-red/80 backdrop-blur-ios flex items-center justify-center">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}

      {/* 删除按钮 - 仅上传模式显示，悬停时显示 */}
      {!isMessageMode && (
        <motion.button
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ios-red dark:bg-ios-dark-red flex items-center justify-center shadow-ios-sm"
          onClick={handleAbort}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: isHovered || fileStatus === 'error' ? 1 : 0, scale: isHovered || fileStatus === 'error' ? 1 : 0.8 }}
          transition={{ duration: 0.2 }}
          aria-label="删除文件"
        >
          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
};

export const IOSFilePreviewItem = memo(IOSFilePreviewItemComponent);
